import { FC, useEffect, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
  mockNewAffiliateSaleNumber,
} from '@features/admin/reports/reportData';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  ReportGroupBy,
  ReportPeriod,
  TReportNewAffiliateSaleNumberData,
} from '@src/types/reports';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    payload: TReportNewAffiliateSaleNumberData;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] flex flex-col items-center min-w-[135px] gap-y-1"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-[#B6D72C] text-xl font-semibold">
          {payload[0]?.value.toLocaleString()}
        </p>
        <p className="text-[#999999] text-[10px]">
          {payload[0]?.payload?.date}
        </p>
        <p className="text-white text-xs">
          Sales: {payload[0]?.value.toLocaleString()}
        </p>
      </div>
    );
  }

  return null;
};

export const NewAffiliateSaleNumber: FC = () => {
  // Data state
  const [data, setData] = useState<TReportNewAffiliateSaleNumberData[]>(
    mockNewAffiliateSaleNumber
  );

  // Use custom hooks for filter management
  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.THIS_YEAR,
    defaultGroupBy: ReportGroupBy.MONTH,
    enableComparisons: true,
  });

  // Use custom hook for table columns - single metric
  const { columns, tableData } =
    useReportTableColumns<TReportNewAffiliateSaleNumberData>({
      data,
      getAccessField: (item) => item.date,
      getMainDataField: (item) => item.commissions || 0,
      customColumns: [
        {
          title: 'SALES',
          className: 'text-right',
          render: (item) => (
            <p className="text-white">
              {item?.commissions?.toLocaleString() ?? 0}
            </p>
          ),
        },
      ],
      includeTotalsRow: true,
    });

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      // TODO: Replace with actual API call when available
      // const payload = buildCommonPayloadWithGroupingAndProducts(filters);
      // const response = await getNewAffiliateSaleNumber(payload);
      // setData(response.data);

      // For now, use mock data
      setData(mockNewAffiliateSaleNumber);
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      filters.setIsLoading(false);
    }
  };

  const handleExportReport = async () => {
    try {
      // TODO: Replace with actual API call when available
      // const payload = buildCommonPayloadWithGroupingAndProducts(filters);
      // const response = await exportNewAffiliateSaleNumber(payload);
      // downloadFile(response.data, 'new-affiliate-sale-number');
      // toast.success('Report exported successfully!');
      console.log('Export report - API not yet implemented');
    } catch (error) {
      console.error('Error exporting report:', error);
    }
  };

  useEffect(() => {
    handleRunReport();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        New Affiliate Sale Number Report
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          dateRangeValue={filters.valueStart}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onDateRangeChange={filters.setValueStart}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={data}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend iconType="line" wrapperStyle={{ fontSize: '14px' }} />
              <Line
                activeDot={{ r: 6 }}
                dataKey="commissions"
                dot={{ fill: '#B6D72C', r: 4 }}
                name="Sales"
                stroke="#B6D72C"
                strokeWidth={2}
                type="monotone"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          <div className="flex justify-end">
            <Button
              className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
              variant="outline"
              onClick={handleExportReport}
            >
              Download CSV
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
